"use client";

import { Modal } from "@/app/(components)/Basic/Modal";
import { Button } from "@/app/(components)/Basic/Button";
import { ArtistVerified } from "@/app/(components)/Basic/ArtistVerified";
import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import { useState } from "react";
import * as Yup from "yup";
import { CurrencyInput } from "@/app/(components)/Basic/CurrencyInput";
import { Form, Formik } from "formik";
import useCurrencyRate from "@/shared/hooks/useCurrencyRate";
import { Contract } from "ethers";
import { ShortUser } from "@/api/users/types";
import { useWeb3State } from "@/web3/context";
import useAccountBalance from "@/shared/hooks/useAccountBalance";
import { useSuccessModal } from "@/app/(components)/Basic/SuccessModal/context/context";
import { useMutation } from "@tanstack/react-query";
import { Type } from "@/app/(components)/Basic/Button/constants";
import { toLowDenom } from "@/shared/currencies";
import { FormRadioButton } from "@/app/(components)/Basic/FormRadioButton";
import useNativePaymentCurrency from "@/shared/hooks/useNativePaymentMethods";
import notificationApi from "@/api/notification/client";
import { ImagePlaceholder } from "@/app/(components)/Basic/Image/types";

export interface Props {
  fullName: string;
  imageUrl: string;
  imagePlaceholder?: ImagePlaceholder;
  isOpen: boolean;
  recipient: ShortUser;
  onClose: () => void;
}

const ArtistTipModal = ({
  fullName,
  imageUrl,
  imagePlaceholder,
  isOpen,
  recipient,
  onClose,
}: Props) => {
  // FIX THIS
  const [select, setSelect] = useState("");
  const currency = useNativePaymentCurrency();

  const { setSuccessData } = useSuccessModal();

  const { data: rateData, isLoading } = useCurrencyRate(currency?.id);
  const { signer, currentAddress } = useWeb3State();
  const { balanceInCrypto } = useAccountBalance(currency?.tokenAddress);

  const { mutateAsync: sendCryptoMutation, isLoading: isSendCryptoLoading } =
    useMutation({
      mutationFn: async ({
        address,
        amount,
      }: {
        address: string;
        amount: number;
      }) => {
        const result = await sendCrypto(address, amount);

        await notificationApi.createTipNotification({
          from: currentAddress!,
          to: address,
          amount: toLowDenom(amount.toString())!,
          paymentErc20TokenAddress: currency!.tokenAddress!,
        });

        return result;
      },
      onSuccess: async () => {
        onClose();

        await setSuccessData({
          text: "Sent tip successfully!",
          item: { name: fullName, media: imageUrl },
        });
      },
      onError: () => {},
    });

  const handleClose = () => {
    onClose();
  };

  const handleOptionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelect(event.target.value);
  };

  const validationSchema = Yup.object().shape({
    tip: Yup.number()
      .min(0.0001, `Min offer is 0.0001 ${currency?.label}`)
      .max(+balanceInCrypto, `You don't have enough ${currency?.label}`)
      .required("Value is required"),
  });

  async function sendCrypto(
    recipientAddress: string,
    amountInToken: number,
    tokenContract?: Contract,
  ) {
    if (!tokenContract) {
      // Send native currency
      const amountInWei = toLowDenom(amountInToken.toString());

      const transaction = {
        to: recipientAddress,
        value: amountInWei,
      };

      const tx = await signer!.sendTransaction(transaction);
      await tx.wait();
    } else {
      // Send ERC20 tokens
      const amountInWei = toLowDenom(amountInToken.toString());

      const tx = await tokenContract!.transfer(recipientAddress, amountInWei);
      await tx.wait();
    }
  }

  const handleSubmit = async (values: any) => {
    const value = values.tip;

    if (signer && recipient.address) {
      await sendCryptoMutation({
        address: recipient.address,
        amount: parseFloat(value),
      });
    }
  };

  const Options = {
    FiveUSD: (5 / rateData?.price).toFixed(4),
    TenUSD: (10 / rateData?.price).toFixed(4),
    FifteenUSD: (15 / rateData?.price).toFixed(4),
    Other: "OTHER",
  };

  return (
    <Modal
      isOpen={isOpen}
      hasCloseButton
      disableClose={isSendCryptoLoading}
      onClose={handleClose}
      className="w-full max-w-[530px]"
    >
      <Formik
        initialValues={{
          address: "",
          tip: undefined,
          paymentErc20TokenAddress: currency?.tokenAddress,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched }) => (
          <Form>
            <section className="flex flex-col items-center gap-y-3 mb-12">
              <div className="font-semibold text-3xl leading-9 text-primary mb-7 tracking-[-1.28px] endemic:invert">
                Give a tip
              </div>
              <ArtistVerified
                imageUrl={imageUrl}
                imagePlaceholder={imagePlaceholder}
                fullName={fullName}
              />
              <div className="text-body-1 night:text-primary">{fullName}</div>
              <div className="font-normal text-secondary text-lg endemic:text-endemicGray-700">
                Artist
              </div>
            </section>
            <section>
              <div className="flex flex-col mb-2">
                <FormRadioButton
                  name="tip"
                  label={`${
                    isLoading ? "..." : Options.FiveUSD
                  } ${currency?.label} (~5.00 USD)`}
                  selected={select}
                  onChange={handleOptionsChange}
                  value={Options.FiveUSD}
                  allThemesWithWhiteBackground
                  disabled={isSendCryptoLoading}
                />
                <FormRadioButton
                  name="tip"
                  label={`${
                    isLoading ? "..." : Options.TenUSD
                  } ${currency?.label} (~10.00 USD)`}
                  selected={select}
                  onChange={handleOptionsChange}
                  value={Options.TenUSD}
                  allThemesWithWhiteBackground
                  disabled={isSendCryptoLoading}
                />
                <FormRadioButton
                  name="tip"
                  label={`${
                    isLoading ? "..." : Options.FifteenUSD
                  } ${currency?.label} (~15.00 USD)`}
                  selected={select}
                  onChange={handleOptionsChange}
                  value={Options.FifteenUSD}
                  allThemesWithWhiteBackground
                  disabled={isSendCryptoLoading}
                />
                <FormRadioButton
                  label="Other amount"
                  selected={select}
                  onChange={handleOptionsChange}
                  value={Options.Other}
                  allThemesWithWhiteBackground
                  disabled={isSendCryptoLoading}
                />
              </div>
              {select === Options.Other && (
                <CurrencyInput
                  label="Tip"
                  name="tip"
                  autoFocus={true}
                  allThemesWithWhiteBackground
                  disablePaymentMethodChange
                  disabled={isSendCryptoLoading}
                />
              )}
              {errors.tip && touched.tip && select !== Options.Other ? (
                <span
                  className={`text-red font-semibold text-xs relative left-4 bottom-2`}
                >
                  {errors.tip}
                </span>
              ) : null}
            </section>
            <section className="flex flex-col items-center  gap-y-10 ">
              {/* TODO => BALANCE HARDCODED */}
              <div className="font-light text-sm leading-4 text-secondary endemic:text-endemicGray-700">
                {`Balance: ${balanceInCrypto} ${currency?.label}`}
              </div>
              <Button
                endIcon={<ArrowRightIcon className="w-4" />}
                disabled={isSendCryptoLoading}
                element={Type.Button}
                type="submit"
              >
                {isSendCryptoLoading ? "Loading" : "Tip the artist"}
              </Button>
            </section>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ArtistTipModal;
